<template lang="pug">
    PrintablePanel
      PageHeader User Usage Report
      .row.mt-3
        .col-8
          | Results for
          strong  {{ startDate }} - {{ endDate }}:
          br
          strong
            |  {{ totalLogins }} Logins, {{ totalSearches }} Searches, {{ totalReports }} Report Queries,
            | {{ totalActiveWatches }} Active Watches, {{ totalWatchHits }} Watch Hits
        .col.text-right(v-if="!printable")
          button.btn.btn-primary.mr-2(@click.prevent="downloadCsv('userusage.csv', csvRecords)") Download CSV
          router-link.btn.btn-primary(:to="{query: {print: true}}" target="_blank") Print
      Datatable.mt-4(
        :columns="columns"
        :header-style="headerStyle"
        :entries="results"
        :per-page="10"
        sortable
        :sort-keys="sortKeys"
        @sortTable="sortResults"
      )
        template(slot-scope="props")
          tr.body-style
            td
              router-link(:to="{name: 'user_search_view', query: {id: props.entry.id}}") View
            td {{ props.entry.username }}
            td {{ props.entry.lastName }}, {{ props.entry.firstName }}
            td {{ removeDuplicationFromArrays(props.entry.states).join(', ') }}
            td {{ props.entry.agencyDescriptions.join(', ') }}
            td {{ props.entry.email }}
            td {{ props.entry.workPhone }}
            td {{ props.entry.lastLogin }}
            td {{ props.entry.lastLoginIp }}
            td {{ props.entry.loginCount }}
            td {{ props.entry.searchCount }}
            td {{ props.entry.reportCount }}
            td {{ props.entry.activeWatches }}/{{ props.entry.watchHits }}
</template>

<script>
import axios from '@/lib/axios'
import PageHeader from '@/components/PageHeader'
import Datatable from '@/components/Datatable'
import PrintablePanel from '@/components/PrintablePanel'
import { globalError, removeDuplicationFromArrays } from '@/lib/utils'
import { formItem, clearForm } from '@/lib/formstore'
import { downloadCsv } from '@/lib/csv'

const sum = require('lodash/sum')

export default {
  name: 'UserUsageReport',
  data() {
    return {
      results: [],
      columns: [
        'Action',
        'User Name',
        'Name',
        'State',
        'Agency',
        'Email',
        'Phone #',
        'Last Login',
        'Last Login IP',
        '# of Logins',
        '# of Searches',
        '# of Report Queries',
        '# of Active Watches/Hits',
      ],
      sortKeys: [
        false,
        'username',
        'lastName',
        'states',
        'agencyDescriptions',
        'email',
        'workPhone',
        'lastLogin',
        'lastLoginIp',
        'loginCount',
        'searchCount',
        'reportCount',
        'activeWatches',
      ],
      headerStyle: {
        fontSize: '10px',
      },
      startDate: formItem('userusage', 'startDate'),
      endDate: formItem('userusage', 'endDate'),
      states: [],
    }
  },
  computed: {
    totalLogins() {
      return sum(this.results.map(result => result.loginCount))
    },
    totalSearches() {
      return sum(this.results.map(result => result.searchCount))
    },
    totalReports() {
      return sum(this.results.map(result => result.reportCount))
    },
    totalActiveWatches() {
      return sum(this.results.map(result => result.activeWatches))
    },
    totalWatchHits() {
      return sum(this.results.map(result => result.watchHits))
    },
    csvRecords() {
      return this.results.map(r => {
        return {
          Username: `"${r.username.replace('"', '""')}"`,
          Name: `"${r.lastName}, ${r.firstName}"`,
          State: `"${removeDuplicationFromArrays(r.states).join(', ')}"`,
          Agency: `"${r.agencyDescriptions.join(', ')}"`,
          Email: r.email || '',
          Phone: r.workPhone || '',
          'Last Login': r.lastLogin || '',
          'Last Login IP': r.lastLoginIp || '',
          '# of Logins': r.loginCount,
          '# of Searches': r.searchCount,
          '# of Report Queries': r.reportCount,
          '# of Active Watches/Hits': `${r.activeWatches}/${r.watchHits}`,
        }
      })
    },
  },
  methods: {
    sortResults({ sortKey, sortDir }) {
      this.results = this.results.sort((a, b) => {
        let A, B
        if (sortKey === 'lastName') {
          A = `${a['lastName']}, ${a['firstName']}`.toLowerCase()
          B = `${b['lastName']}, ${b['firstName']}`.toLowerCase()
        } else if (sortKey === 'agencyDescriptions') {
          A = a[sortKey][0].toLowerCase()
          B = b[sortKey][0].toLowerCase()
        } else if (sortKey === 'activeWatches') {
          A = a['activeWatches']
          B = b['activeWatches']
          // If sort sorting by active watches and the number of active watches are the same, then
          // sort by watch hits instead.
          if (A === B) {
            A = a['watchHits']
            B = b['watchHits']
          }
        } else {
          A =
            typeof a[sortKey] === 'string'
              ? a[sortKey].toLowerCase()
              : a[sortKey] || ''
          B =
            typeof b[sortKey] === 'string'
              ? b[sortKey].toLowerCase()
              : b[sortKey] || ''
        }

        if (sortDir === 'asc') return A < B ? -1 : A > B ? 1 : 0
        else return A < B ? 1 : A > B ? -1 : 0
      })
    },
    print() {
      this.$route.to.params.print = true
    },
    downloadCsv,
    removeDuplicationFromArrays,
  },
  beforeRouteEnter(_to, _from, next) {
    axios()
      .post('/api/admin/user_usage_report', {
        username: formItem('userusage', 'username'),
        lastName: formItem('userusage', 'lastName'),
        email: formItem('userusage', 'email'),
        statesList: formItem('userusage', 'selectedStates').map(
          s => s.abbreviation,
        ),
        agencies:
          formItem('userusage', 'agencyType') === 'all'
            ? []
            : formItem('userusage', 'selectedAgencies').map(a => a.id),
        status: formItem('userusage', 'status'),
        startDate: formItem('userusage', 'startDate'),
        endDate: formItem('userusage', 'endDate'),
      })
      .then(resp => {
        scrollTo(0, 0)
        next(vm => {
          vm.results = resp.data
          if (vm.printable) vm.$nextTick(() => window.print())
        })
      })
      .catch(err => globalError(err))
  },
  components: { PageHeader, Datatable, PrintablePanel },
}
</script>

<style lang="scss" scoped>
.body-style {
  font-size: 11px;
}
</style>
